/* eslint-disable replo/no-tailwind-literal-color */

import React, { useEffect, useMemo, useState } from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";

import { CANVAS_DATA } from "@/features/canvas/canvas-constants";
import { useWebContainer } from "@/features/web-container/context";
import { useCachedPageContent } from "@/features/web-container/page-cache";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import twMerge from "@replo/design-system/utils/twMerge";
import { useParams } from "react-router-dom";
import useMeasure from "react-use-measure";
import { ReploError } from "schemas/errors";

import { useReactRenderStore } from "../store/reactRenderStore";
import { useViewportStore } from "../store/viewportStore";
import TipTapOverlay from "./TipTapOverlay";

type PageRendererProps = {
  pageRoute: string;
  className?: string;
  isLoadingPage?: boolean;
  showControls?: boolean;
  previewThumbnail?: boolean;
};

class PageRouteRequiredError extends ReploError {
  constructor(pageRoute: string) {
    super({
      message: "Page route not found",
      additionalData: { pageRoute },
    });
  }
}

const MAX_RENDERS_BEFORE_ERROR = 5;
/**
 * This hook is used to get the page URL from the web container. It also handles
 * the case where the page is not yet found, requiring a rerender of the iframe
 * while waiting for the next dev server to catch up.
 *
 * NOTE (Gabe 2025-07-07): The page will not transition from isLoading until the
 * pageUrl is requested (for instance via an iframe).
 */
const usePageFromWebContainer = (pageRoute: string) => {
  const [initialRenderAt] = useState(Date.now());
  const [rerenderCount, setForceRerender] = useState(0);

  const { webContainerUrl, pageStatusMap } = useWebContainer();
  const pageUrl = useMemo(() => {
    return webContainerUrl && pageRoute
      ? `${webContainerUrl}/${pageRoute.replace(/^\//, "")}`
      : undefined;
  }, [pageRoute, webContainerUrl]);

  // NOTE (Gabe 2025-07-07): We use timestamp here to determine if this status
  // is a result of a request made for this render attempt (of PageRenderer) or
  // a previous render. If the timestamp indicates the status is from a previous
  // request, we treat this as loading while we wait for the next dev server to
  // serve the page. We also treat 404 as loading because it's usually an
  // indication that the page is not yet available (next dev server is still
  // catching up) (unless we've already rerendered 5 times).
  const isLoading =
    !pageStatusMap[pageRoute] ||
    pageStatusMap[pageRoute].timestamp < initialRenderAt ||
    (pageStatusMap[pageRoute]?.status === 404 &&
      rerenderCount < MAX_RENDERS_BEFORE_ERROR);

  const isError = !isLoading && pageStatusMap[pageRoute]?.status !== 200;

  useEffect(() => {
    if (
      pageStatusMap[pageRoute]?.status === 404 &&
      pageStatusMap[pageRoute]?.timestamp > initialRenderAt
    ) {
      const timeout = setTimeout(() => {
        setForceRerender((prev) =>
          prev < MAX_RENDERS_BEFORE_ERROR ? prev + 1 : prev,
        );
      }, 100);

      return () => clearTimeout(timeout);
    }
  }, [pageStatusMap, pageRoute, initialRenderAt]);

  return { pageUrl, isLoading, isError, iframeKey: rerenderCount };
};

function PageRenderer({
  pageRoute,
  className,
  showControls = true,
  previewThumbnail,
}: PageRendererProps) {
  const {
    selectedElement,
    setSelectedElement,
    setEditText,
    setClassNames,
    setSectionClicked,
    displayMode,
  } = useReactRenderStore();
  if (!pageRoute) {
    throw new PageRouteRequiredError(pageRoute);
  }

  const projectId = useCurrentProjectId();
  const { pageId } = useParams();

  const html = useCachedPageContent({ pageRoute, projectId });

  const { pageUrl, isLoading, isError, iframeKey } =
    usePageFromWebContainer(pageRoute);

  const currentViewport = useViewportStore((state) => state.currentViewport);

  const [containerRef, containerDimensions] = useMeasure();

  const hoverRef = React.useRef<HTMLDivElement>(null);
  const clickedSectionRef = React.useRef<HTMLDivElement>(null);
  const tiptapOverlayRef = React.useRef<HTMLDivElement>(null);

  // Get viewport-specific width from CANVAS_DATA
  const viewportWidth = CANVAS_DATA[currentViewport].defaultFrameWidth;

  // Calculate scale based on context and measured container size
  const scale = React.useMemo(() => {
    if (previewThumbnail) {
      return 0.25;
    }

    if (containerDimensions.width > 0) {
      // Scale viewport content to fit the container width exactly
      return containerDimensions.width / viewportWidth;
    }

    return 1; // Default to no scaling
  }, [containerDimensions.width, viewportWidth, previewThumbnail]);

  const latestRectRef = React.useRef<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);

  useEffect(() => {
    if (selectedElement && showControls) {
      setEditText(selectedElement.childText);
      setClassNames(
        selectedElement.props.className?.split(" ").filter(Boolean).join(" ") ??
          "",
      );
    }
    if (selectedElement === null && showControls) {
      // Find the iframe (adjust selector as needed)
      const iframe = document.querySelector("iframe");
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage({ type: "CLEAR_SELECTION" }, "*");
      }
    }
  }, [selectedElement, setEditText, showControls, setClassNames]);

  // Send display mode to iframe when it changes
  useEffect(() => {
    const iframe = document.querySelector("iframe");
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage(
        {
          type: "SET_DISPLAY_MODE",
          payload: { displayMode },
        },
        "*",
      );
    }
  }, [displayMode]);

  useEffect(() => {
    if (!showControls) {
      return;
    }

    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === "FIBER_SELECTED") {
        setSelectedElement(event.data.payload);
        latestRectRef.current = event.data.payload;
      }
      if (event.data.type === "SECTION_CLICKED") {
        setSectionClicked(true);
      }
      if (event.data.type === "NON_SECTION_CLICKED") {
        setSectionClicked(false);
      }
      if (event.data?.type === "HOVER_ELEMENT" && hoverRef.current) {
        const rect = event.data.payload.rect;

        if (rect) {
          Object.assign(hoverRef.current.style, {
            top: `${rect.top}px`,
            left: `${rect.left}px`,
            width: `${rect.width}px`,
            height: `${rect.height}px`,
            display: "block",
          });
        } else {
          hoverRef.current.style.display = "none";
        }
      }
      if (event.data?.type === "SELECTED_ELEMENT_SCROLL_UPDATE") {
        const rect = event.data.payload.rect;
        if (rect) {
          latestRectRef.current = rect;
        }
      }
    };

    if (displayMode === "select") {
      void window.addEventListener("message", handleMessage);
      return () => window.removeEventListener("message", handleMessage);
    }
  }, [setSelectedElement, showControls, setSectionClicked, displayMode]);

  useEffect(() => {
    if (!showControls) {
      return;
    }

    let animationFrameId: number;

    const updateOverlay = () => {
      const rect = latestRectRef.current;

      if (rect) {
        const { top, left, width, height } = rect;

        // Update red border if it exists
        if (clickedSectionRef.current) {
          Object.assign(clickedSectionRef.current.style, {
            top: `${top}px`,
            left: `${left}px`,
            width: `${width}px`,
            height: `${height}px`,
            display: "block",
          });
        }

        // Update TipTap overlay if it exists
        if (tiptapOverlayRef.current) {
          Object.assign(tiptapOverlayRef.current.style, {
            top: `${top}px`,
            left: `${left}px`,
            width: `${width}px`,
            height: `${height}px`,
            display: "block",
          });
        }
      }
      animationFrameId = requestAnimationFrame(updateOverlay);
    };

    animationFrameId = requestAnimationFrame(updateOverlay);

    return () => cancelAnimationFrame(animationFrameId);
  }, [showControls]);

  return (
    <div
      ref={containerRef}
      className={twMerge("flex h-full w-full flex-col", className)}
    >
      <div
        className={twMerge("relative h-full w-full overflow-hidden", className)}
      >
        <div
          style={{
            transformOrigin: "top left",
            transform: `scale(${scale})`,
            width: `${100 / scale}%`,
            height: `${100 / scale}%`,
            overflow: "hidden",
          }}
          className="flex h-full flex-col"
        >
          <div className="flex h-full w-full flex-col">
            <div className="relative flex h-full w-full flex-col overflow-y-auto">
              {isLoading && !html && (
                <Skeleton
                  className="h-full w-full rounded absolute"
                  speed={2}
                  backgroundColor="#f1f5f9"
                  foregroundColor="#e2e8f0"
                />
              )}
              {isLoading && html && (
                <iframe
                  sandbox=""
                  srcDoc={html}
                  className="h-full w-full rounded absolute"
                />
              )}
              {isError && (
                <div className="absolute inset-0 flex items-center text-6xl justify-center text-red-500 bg-red-50">
                  Error loading page
                </div>
              )}
              <iframe
                src={pageUrl}
                key={iframeKey}
                className="h-full w-full rounded-none"
              />
            </div>
          </div>
          {/* Overlay Container */}
          <div className="absolute inset-0 pointer-events-none z-[99999]">
            <div
              ref={hoverRef}
              className="absolute border-2 border-dashed border-blue-500 pointer-events-none"
              style={{
                top: 0,
                left: 0,
                width: 0,
                height: 0,
                display: "none",
              }}
            />
            {selectedElement && (
              <div
                ref={clickedSectionRef}
                className="absolute border-1 border-red-500 pointer-events-none"
                style={{
                  top: 0,
                  left: 0,
                  width: 0,
                  height: 0,
                  display: "none",
                }}
              />
            )}
            {selectedElement && selectedElement.childText && (
              <TipTapOverlay
                ref={tiptapOverlayRef}
                initialContent={selectedElement.childText}
                className={selectedElement.props.className}
                styleOverrides={selectedElement.styleOverrides}
                page={{ pageRoute, id: pageId ?? "" }}
                projectId={projectId ?? ""}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PageRenderer;
